// tabs/VenuesTab.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Store as StoreIcon,
  MoreVert as MoreVertIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { API_BASE_URL } from 'config/api';
import {
  SectionCard,
  SectionTitle,
  ActionButton,
  EmptyState,
  honeyColors,
} from '../styled';

const VenuesTab = ({ venues, community }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, venue: null });

  const getImageUrl = (image) => {
    if (!image) return null;
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  const handleMenuOpen = (event, venue) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedVenue(venue);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVenue(null);
  };

  const handleViewVenue = (venue) => {
    navigate(`/venue/${venue.slug}`);
  };

  const handleManageTariffs = (venue) => {
    // Navigate to tariff management for this venue-community combination
    navigate(`/manage/venue/${venue.slug}/tariffs?community=${community.slug}`);
    handleMenuClose();
  };

  const handleOpenDetail = (venue) => {
    setDetailDialog({ open: true, venue });
  };

  const handleCloseDetail = () => {
    setDetailDialog({ open: false, venue: null });
  };

  if (venues.length === 0) {
    return (
      <EmptyState>
        <StoreIcon sx={{ fontSize: 80, color: honeyColors.border, mb: 2 }} />
        <Typography variant="h5" color="textSecondary" gutterBottom>
          No Partner Venues Yet
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 3, maxWidth: 400 }}>
          Partner venues offer special pricing and benefits to your community members.
        </Typography>
        <ActionButton variant="contained" startIcon={<AddIcon />}>
          Add Partner Venue
        </ActionButton>
      </EmptyState>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: honeyColors.textPrimary }}>
            Partner Venues ({venues.length})
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage venues that offer special pricing to your community
          </Typography>
        </Box>
        <ActionButton variant="contained" startIcon={<AddIcon />}>
          Add Venue
        </ActionButton>
      </Box>

      {/* Venues Grid */}
      <Grid container spacing={3}>
        {venues.map((venue) => (
          <Grid item xs={12} sm={6} md={4} key={venue.slug}>
            <Card
              sx={{
                borderRadius: '16px',
                border: `1px solid ${honeyColors.border}`,
                boxShadow: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: honeyColors.primary,
                  boxShadow: honeyColors.shadowLight,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => handleOpenDetail(venue)}
            >
              <CardMedia
                component="div"
                sx={{
                  height: 160,
                  backgroundColor: honeyColors.surfaceHoney,
                  backgroundImage: venue.image ? `url(${getImageUrl(venue.image)})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!venue.image && (
                  <StoreIcon sx={{ fontSize: 64, color: honeyColors.border }} />
                )}
              </CardMedia>
              
              <CardContent sx={{ flex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {venue.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, venue)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                {venue.single_address && (
                  <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <LocationIcon sx={{ fontSize: 18, color: honeyColors.textMuted, mt: 0.3 }} />
                    <Typography variant="body2" color="textSecondary">
                      {venue.single_address}
                    </Typography>
                  </Box>
                )}
                
                {venue.city && (
                  <Chip
                    label={venue.city}
                    size="small"
                    sx={{
                      backgroundColor: honeyColors.surfaceHoney,
                      color: honeyColors.textSecondary,
                      fontWeight: 500,
                    }}
                  />
                )}
              </CardContent>
              
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<PriceIcon />}
                  sx={{ color: honeyColors.primary }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleManageTariffs(venue);
                  }}
                >
                  Manage Pricing
                </Button>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewVenue(venue);
                  }}
                >
                  View
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Venue Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleViewVenue(selectedVenue); handleMenuClose(); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View Venue</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleManageTariffs(selectedVenue)}>
          <ListItemIcon><PriceIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Manage Pricing</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Partnership Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: honeyColors.error }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: honeyColors.error }} /></ListItemIcon>
          <ListItemText>Remove Partnership</ListItemText>
        </MenuItem>
      </Menu>

      {/* Venue Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
      >
        {detailDialog.venue && (
          <>
            <Box
              sx={{
                height: 200,
                backgroundColor: honeyColors.surfaceHoney,
                backgroundImage: detailDialog.venue.image ? `url(${getImageUrl(detailDialog.venue.image)})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <DialogTitle sx={{ pt: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {detailDialog.venue.name}
              </Typography>
              {detailDialog.venue.city && (
                <Chip
                  label={detailDialog.venue.city}
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
            </DialogTitle>
            <DialogContent>
              {detailDialog.venue.description && (
                <Typography variant="body1" paragraph>
                  {detailDialog.venue.description}
                </Typography>
              )}
              
              <Box sx={{ mt: 2 }}>
                {detailDialog.venue.single_address && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <LocationIcon sx={{ color: honeyColors.primary }} />
                    <Typography>{detailDialog.venue.single_address}</Typography>
                  </Box>
                )}
                
                {detailDialog.venue.contact_phone_1 && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <PhoneIcon sx={{ color: honeyColors.primary }} />
                    <Typography>{detailDialog.venue.contact_phone_1}</Typography>
                  </Box>
                )}
                
                {detailDialog.venue.contact_email && (
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <EmailIcon sx={{ color: honeyColors.primary }} />
                    <Typography>{detailDialog.venue.contact_email}</Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetail}>Close</Button>
              <ActionButton
                variant="contained"
                onClick={() => {
                  handleViewVenue(detailDialog.venue);
                  handleCloseDetail();
                }}
              >
                View Full Details
              </ActionButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default VenuesTab;
